import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { 
  ArrowLeftIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  CubeIcon,
  CurrencyDollarIcon,
  ClockIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const ReportView = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/price-book/reports/${id}/items`);
      setReport(response.data);
    } catch (err) {
      console.error('Error fetching report:', err);
      if (err.response?.status === 404) {
        setError('Report not found');
      } else {
        setError('Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatQuantityRange = (min, max) => {
    if (max === null || max === undefined) {
      return `${min}+`;
    }
    if (min === max) {
      return min.toString();
    }
    return `${min} - ${max}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFrequencyDisplay = (frequency) => {
    if (!frequency) return 'One-time';
    
    if (frequency.days_interval === 0) return 'One-time';
    if (frequency.days_interval === 1) return 'Daily';
    if (frequency.days_interval === 7) return 'Weekly';
    if (frequency.days_interval === 14) return 'Bi-weekly';
    if (frequency.days_interval === 30) return 'Monthly';
    if (frequency.days_interval === 90) return 'Quarterly';
    if (frequency.days_interval === 180) return 'Semi-annually';
    if (frequency.days_interval === 365) return 'Annually';
    
    return frequency.display_label || frequency.frequency_name;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={() => navigate('/price-book/reports')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-8">
        <div className="sm:flex-auto">
          <div className="flex items-center space-x-3 mb-4">
            <button
              onClick={() => navigate('/price-book/reports')}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Reports
            </button>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">{report.name}</h1>
          {report.description && (
            <p className="mt-2 text-sm text-gray-700">{report.description}</p>
          )}
          <div className="mt-2 text-sm text-gray-500">
            Created by {report.created_by_username} on {formatDate(report.created_at)}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex space-x-3">
            <button
              onClick={() => alert('Export functionality coming soon!')}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export
            </button>
            <Link
              to={`/price-book/reports/${report.id}/manage`}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <Cog6ToothIcon className="h-4 w-4 mr-2" />
              Manage Items
            </Link>
            <Link
              to={`/price-book/reports/${report.id}/edit`}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Report
            </Link>
          </div>
        </div>
      </div>

      {/* Report Summary */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg p-6 mb-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CubeIcon className="h-8 w-8 text-gray-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Items</dt>
                <dd className="text-lg font-medium text-gray-900">{report.items.length}</dd>
              </dl>
            </div>
          </div>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-gray-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Price Range</dt>
                <dd className="text-lg font-medium text-gray-900">
                  {report.items.length > 0 ? (
                    (() => {
                      const allPrices = report.items.flatMap(item => 
                        item.tiers.flatMap(tier => [
                          tier.base_price,
                          tier.tier_price
                        ]).filter(price => price !== null && price !== undefined)
                      );
                      if (allPrices.length === 0) return 'No pricing';
                      const min = Math.min(...allPrices);
                      const max = Math.max(...allPrices);
                      return min === max ? formatCurrency(min) : `${formatCurrency(min)} - ${formatCurrency(max)}`;
                    })()
                  ) : 'No items'}
                </dd>
              </dl>
            </div>
          </div>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-gray-400" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Last Updated</dt>
                <dd className="text-lg font-medium text-gray-900">{formatDate(report.updated_at)}</dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Items List */}
      {report.items.length === 0 ? (
        <div className="text-center py-12 bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg">
          <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No items in this report</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add items to this report to see pricing information.
          </p>
          <div className="mt-6">
            <Link
              to={`/price-book/reports/${report.id}/manage`}
              className="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <Cog6ToothIcon className="h-4 w-4 mr-2" />
              Manage Items
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {report.items.map((item, index) => (
            <div key={item.id} className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
              {/* Item Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{item.item_name}</h3>
                    {item.description && (
                      <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                    )}
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>Category: {item.category_display_label || 'Uncategorized'}</span>
                      <span>Unit: {item.unit_display_label || 'N/A'}</span>
                      {item.is_recurring && (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          Recurring
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Item #{index + 1}</div>
                    {item.tiers.length > 0 && (
                      <div className="text-sm text-gray-500 mt-1">
                        {item.tiers.length} pricing tier{item.tiers.length !== 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Pricing Tiers */}
              {item.tiers.length === 0 ? (
                <div className="px-6 py-4 text-center text-gray-500">
                  No pricing tiers configured for this item
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity Range
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Base Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Per Unit Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Setup Fee
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tier Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Frequency
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {item.tiers.map((tier) => (
                        <tr key={tier.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {tier.tier_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatQuantityRange(tier.min_quantity, tier.max_quantity)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {formatCurrency(tier.base_price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tier.per_unit_price ? formatCurrency(tier.per_unit_price) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tier.setup_fee ? formatCurrency(tier.setup_fee) : 'None'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {tier.tier_price ? formatCurrency(tier.tier_price) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {getFrequencyDisplay(tier)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportView; 